import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation as i18nUseTranslation } from 'react-i18next';

import useTranslation from '@src/hooks/useTranslation';
import Modal from '@src/components/common/modal';
import Footer from '@src/components/common/modalFooter';
import { LanguageCHTIcon } from '@src/components/common/icons/LanguageCHTIcon';
import { LanguageENIcon } from '@src/components/common/icons/LanguageENIcon';
import useLanguageSelection from '@src/hooks/useLanguageSelection';
import { normalizeLanguage } from '@src/i18n/languagePolicy';
import { LANGUAGE_NAME_I18N_KEYS } from '@src/const/common';

const MODAL_WIDTH = {
	desktop: 288,
	tablet: 288,
};

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const StyledModal = styled(Modal)`
	#modal-body {
		padding-bottom: 20px;
		padding-top: 44px;
	}
`;

const StyledFooter = styled(Footer)`
	.submit-button {
		width: 100%;
	}
	.button-wrapper {
		width: 100%;
	}
`;

const LanguageList = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const LanguageText = styled.div`
	cursor: pointer;
	width: 100%;
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 21px;
	color: ${props => (props.isActive ? '#3091FD' : '#222222')};
	margin-bottom: 24px;
	display: flex;
	align-items: flex-start;
	justify-content: flex-start;
	svg {
		margin-right: 14px;
	}
`;

interface ILanguageSettingModal {
	isActive: boolean;
	handleClose: () => void;
	headerText: string;
}

const LanguageSettingModal = ({
	isActive,
	headerText,
	handleClose,
}: ILanguageSettingModal):JSX.Element => {
	const { t } = useTranslation();
	const { selectLanguage } = useLanguageSelection();
	const { i18n } = i18nUseTranslation();
	const [language, setLanguage] = useState(normalizeLanguage(i18n.language));

	useEffect(() => {
		if (isActive) setLanguage(normalizeLanguage(i18n.language));
	}, [isActive, i18n.language]);

	return (
		<StyledModal
			disableScroll={false}
			modalWidth={MODAL_WIDTH}
			isActive={isActive}
			headerText={headerText}
			handleClose={handleClose}
			footer={(
				<StyledFooter
					submitText={t('common:action:confirm')}
					handleSubmit={() => {
						selectLanguage(language);
						handleClose();
					}}
					showCancel={false}
				/>
			)}
		>
			<ModalContent>
				<LanguageList>
					<LanguageText
						isActive={language === 'zh-CHT'}
						onClick={() => {
							setLanguage('zh-CHT');
						}}
					>
						<LanguageCHTIcon />
						{t(LANGUAGE_NAME_I18N_KEYS['zh-tw'])}
					</LanguageText>
					<LanguageText
						isActive={language === 'en'}
						onClick={() => {
							setLanguage('en');
						}}
					>
						<LanguageENIcon />
						{t(LANGUAGE_NAME_I18N_KEYS.en)}
					</LanguageText>
				</LanguageList>
			</ModalContent>
		</StyledModal>
	);
};

export default LanguageSettingModal;
